import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom Dropdown Component
 * Replaces native <select> for a consistent, styled "Enterprise" look.
 * 
 * Props:
 * - value: current selected value
 * - options: array of { value, label, style? } objects
 * - onChange: callback(newValue)
 * - minWidth: CSS string (default '120px')
 * - placeholder: text to show if no value selected
 * - className: additional CSS classes for the container
 */
const CustomDropdown = ({ value, options, onChange, minWidth = '120px', placeholder = 'Select...', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find(o => o.value === value);
  const selectedLabel = selectedOption?.label || value || placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef} style={{ minWidth }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2.5 text-white flex items-center justify-between gap-2 transition-all active:scale-[0.98] outline-none focus:ring-2 focus:ring-white/20"
      >
        <span className="font-medium text-sm truncate">{selectedLabel}</span>
        <span className="text-white/50 text-xs transition-transform duration-200" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-[var(--pbfs-navy)] border border-white/20 rounded-xl shadow-2xl overflow-hidden z-50 animate-scaleIn origin-top">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                style={option.style}
                className={`w-full text-left px-4 py-3 text-sm border-b border-white/5 last:border-0 hover:bg-white/10 transition-colors flex items-center justify-between ${
                  value === option.value 
                    ? 'bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] font-bold' 
                    : 'text-white/80'
                }`}
              >
                <span>{option.label}</span>
                {value === option.value && <span>✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;

